// Who can use AI Studio (API: GET api/AI/Access, set by admins in Admin Panel > AI Studio access).
//  - useAIAccess(): { ready, allowed, show, locked, upgradeUrl, studioUrl } for the current user
//  - openAIStudio(access, setDivName, keyword): opens AI Studio, the new app (studioUrl), or the upgrade message
//  - AIStudioGate: shows AI Studio only to users who have it
// When the API does not have this yet (not deployed) or cannot be reached, everybody keeps AI Studio, as before.

import React, { useContext, useEffect, useState } from "react";
import Swal from "sweetalert2";
import AuthContext from "../context/userAuth.context";
import { aiApi } from "../Utils/aiTemplate/aiApi";

const OPEN = { ready: true, allowed: true, show: true, locked: false, upgradeUrl: null, studioUrl: null };
const WAITING = { ...OPEN, ready: false, show: false };
const LIFE_MS = 5 * 60 * 1000;

let kept = null; // { key, at, value }
let pending = null; // { key, promise }
const listeners = new Set();

function tokenKey(api) {
  try {
    const data = JSON.parse(localStorage.getItem("animation_studio_2.0_access_token"));
    return api + "|" + ((data && data.access_token) || "").slice(-24);
  } catch (e) {
    return api + "|";
  }
}

/** The current user's AI Studio access (kept for 5 minutes; fresh = ask the server again). */
export function loadAIAccess(api, fresh = false) {
  if (!api) return Promise.resolve(OPEN);
  const key = tokenKey(api);
  if (!fresh && kept && kept.key === key && Date.now() - kept.at < LIFE_MS) return Promise.resolve(kept.value);
  if (!fresh && pending && pending.key === key) return pending.promise;
  const promise = aiApi.access(api).then((res) => {
    const value =
      res && res.status
        ? {
            ready: true,
            allowed: !!res.allowed,
            show: !!res.show,
            locked: !!res.locked,
            upgradeUrl: res.upgradeUrl || null,
            studioUrl: res.studioUrl || null,
          }
        : OPEN; // not deployed / no answer: as before
    if (res && res.status) kept = { key, at: Date.now(), value };
    if (pending && pending.promise === promise) pending = null;
    listeners.forEach((fn) => fn(value));
    return value;
  });
  pending = { key, promise };
  return promise;
}

export function useAIAccess() {
  const { authenticationData } = useContext(AuthContext) || {};
  const api = authenticationData && authenticationData.ApiBaseURL;
  const [access, setAccess] = useState(() =>
    kept && api && kept.key === tokenKey(api) ? kept.value : WAITING
  );
  useEffect(() => {
    let off = false;
    const take = (v) => !off && setAccess(v);
    listeners.add(take);
    loadAIAccess(api).then(take);
    return () => {
      off = true;
      listeners.delete(take);
    };
  }, [api]);
  return access;
}

const safeUrl = (url) => {
  try {
    const u = new URL(url);
    return u.protocol === "https:" || u.protocol === "http:" ? u.toString() : null;
  } catch (e) {
    return null;
  }
};

export function showAIUpgrade(access) {
  const url = access && safeUrl(access.upgradeUrl);
  const link = url ? `<a href="${url.replace(/"/g, "%22")}" target="_blank" rel="noopener noreferrer">upgrade</a>` : null;
  Swal.fire({
    title: "Upgrade Required",
    html: link
      ? `AI Studio is not part of your plan yet. Please ${link} to unlock it.`
      : "AI Studio is not part of your plan yet. Please contact support to unlock it.",
    icon: "info",
    cancelButtonColor: "#d33",
    backdrop: null,
  });
}

/** Opens the new AI Studio app in a new tab (with the keyword, if any). */
export function openStudioApp(studioUrl, keyword) {
  const url = safeUrl(studioUrl);
  if (!url) return false;
  const u = new URL(url);
  if (keyword) u.searchParams.set("q", keyword);
  window.open(u.toString(), "_blank", "noopener");
  return true;
}

/**
 * What a click on "AI Studio" does: the upgrade message when the user does not have it, the new app
 * when admins set its address, otherwise the AI Studio page inside Toons. true = the page was opened.
 */
export function openAIStudio(access, setDivName, keyword) {
  if (access && access.locked) {
    showAIUpgrade(access);
    return false;
  }
  if (access && access.studioUrl && openStudioApp(access.studioUrl, keyword)) return false;
  if (setDivName) setDivName("AI Studio");
  return true;
}

const box = {
  maxWidth: 560,
  margin: "40px auto",
  padding: "32px 28px",
  borderRadius: 16,
  background: "#fff",
  border: "1px solid #e7e3f5",
  boxShadow: "0 12px 32px -18px rgba(76, 29, 149, .35)",
  textAlign: "center",
  fontFamily: "inherit",
};
const btn = {
  marginTop: 18,
  height: 42,
  padding: "0 22px",
  border: 0,
  borderRadius: 10,
  background: "linear-gradient(135deg, #7c3aed, #db2777)",
  color: "#fff",
  fontSize: 15,
  fontWeight: 700,
  fontFamily: "inherit",
  cursor: "pointer",
};

/** AI Studio for users who have it; the upgrade box (or "opens in its own window") for the others. */
export function AIStudioGate({ children }) {
  const access = useAIAccess();
  if (!access.ready) return <div style={{ padding: 48, textAlign: "center", color: "#7a7891" }}>Loading AI Studio…</div>;
  if (access.allowed && !access.studioUrl) return children;
  if (access.allowed) {
    return (
      <div style={box}>
        <span className="material-icons" style={{ fontSize: 40, color: "#7c3aed" }}>auto_awesome</span>
        <h3 style={{ margin: "10px 0 6px" }}>AI Studio opens in its own window</h3>
        <div style={{ color: "#5d5a73" }}>Make templates from a few words, on any device.</div>
        <button style={btn} onClick={() => openStudioApp(access.studioUrl)}>Open AI Studio</button>
      </div>
    );
  }
  return (
    <div style={box}>
      <span className="material-icons" style={{ fontSize: 40, color: "#7c3aed" }}>lock</span>
      <h3 style={{ margin: "10px 0 6px" }}>Unlock AI Studio</h3>
      <div style={{ color: "#5d5a73" }}>
        Turn a few words into a ready-made animated template with scenes, texts and a talking presenter.
        AI Studio is not part of your plan yet.
      </div>
      {access.upgradeUrl && safeUrl(access.upgradeUrl) ? (
        <button style={btn} onClick={() => window.open(safeUrl(access.upgradeUrl), "_blank", "noopener")}>Upgrade</button>
      ) : null}
    </div>
  );
}
